<template>
  <div v-if="offer != undefined" class="w-full h-full">
    <!-- Reusable confirmation modal for activate / deactivate / remove -->
    <AlertDialog :open="confirmModal.open" @update:open="(v) => !v && (confirmModal.open = false)">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ confirmModal.title }}</AlertDialogTitle>
          <AlertDialogDescription>{{ confirmModal.description }}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter class="gap-2">
          <AlertDialogCancel @click="confirmModal.open = false">Cancel</AlertDialogCancel>
          <AlertDialogAction @click="confirmProceed()">Confirm</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog
      v-if="showFormModal"
      :open="showFormModal"
      @update:open="(v) => (showFormModal = v)"
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Request approval</AlertDialogTitle>
        </AlertDialogHeader>
        <div v-if="!offer.cashbackRules[0].conditions[0].fixedDistributorCommission">
          <p class="text-sm">Set cashback split</p>
          <label class="text-sm block mt-2"
            >Your users cashback split percentage
            <Input
              v-model="activateOfferRequest.splitOfCashbackPercentage.endUser"
              :min="0"
              :max="100"
              type="number"
            />
          </label>
          <label class="text-sm block mt-2"
            >Your commission split percentage
            <Input
              v-model="activateOfferRequest.splitOfCashbackPercentage.client"
              type="number"
              :min="0"
              :max="100"
              :disabled="true"
            />
          </label>
          <label class="text-sm block mt-2"
            >Offer avaliable to:
            <Select v-model="activateOfferRequest.customerTierType">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Offer avaliable to" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in trueOrFalse" :key="String(o.id)" :value="o.id">{{
                  o.text
                }}</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <div v-if="activateOfferRequest.customerTierType == 'TIERED'" class="mt-2">
            <p class="text-sm font-medium">Select customer Tiers</p>
            <label
              v-for="(s, idx) in customerSegments"
              :key="idx"
              class="flex items-center gap-2 text-sm"
            >
              <Checkbox
                :model-value="selectedCustomerTiers.some((x) => x.id === s.id)"
                @update:model-value="
                  (v) => {
                    if (v) selectedCustomerTiers.push(s)
                    else selectedCustomerTiers = selectedCustomerTiers.filter((x) => x.id !== s.id)
                  }
                "
              />
              {{ s.text }}
            </label>
          </div>
        </div>
        <div v-else>
          <label
            v-for="(s, idx) in giveAllCommission"
            :key="idx"
            class="flex items-center gap-2 text-sm mt-2"
          >
            <Checkbox
              :model-value="selectedCommissionType.some((x) => x.id === s.id)"
              @update:model-value="
                (v) => {
                  if (v) selectedCommissionType.push(s)
                  else selectedCommissionType = selectedCommissionType.filter((x) => x.id !== s.id)
                }
              "
            />
            {{ s.text }}
          </label>
          <label class="text-sm block mt-2"
            >Make this offer avaliable to
            <Select v-model="activateOfferRequest.customerTierType">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Make this offer avaliable to" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in trueOrFalse" :key="String(o.id)" :value="o.id">{{
                  o.text
                }}</SelectItem>
              </SelectContent>
            </Select>
          </label>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel @click="showFormModal = false">Cancel</AlertDialogCancel>
          <AlertDialogAction
            :disabled="
              activateOfferRequest.splitOfCashbackPercentage.client > 100 ||
              activateOfferRequest.splitOfCashbackPercentage.client < -2
            "
            @click="
              () => {
                showFormModal = false
                requestApproval()
              }
            "
            >Request approval</AlertDialogAction
          >
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog
      v-if="showEditFormModal"
      :open="showEditFormModal"
      @update:open="(v) => (showEditFormModal = v)"
    >
      <AlertDialogContent>
        <!-- The sub-title belongs inside the header; as a loose <p> it picked up the
             dialog's own gap and sat far too far from the title. -->
        <AlertDialogHeader>
          <AlertDialogTitle>Edit offer</AlertDialogTitle>
          <AlertDialogDescription>Offer may require re-approval if changed</AlertDialogDescription>
        </AlertDialogHeader>
        <div
          v-if="!offer.cashbackRules[0].conditions[0].fixedDistributorCommission"
          class="grid gap-4"
        >
          <p class="text-sm font-medium">Set cashback split</p>
          <label class="grid gap-1.5 text-sm"
            >Your users cashback split percentage
            <Input
              v-model="activateOfferRequest.splitOfCashbackPercentage.endUser"
              :min="0"
              :max="100"
              type="number"
            />
          </label>
          <label class="grid gap-1.5 text-sm"
            >Your commission split percentage
            <Input
              v-model="activateOfferRequest.splitOfCashbackPercentage.client"
              type="number"
              :min="0"
              :max="100"
              :disabled="true"
            />
          </label>
          <div class="grid gap-1.5">
            <span class="text-sm">Make this offer available to</span>
            <Select v-model="activateOfferRequest.customerTierType">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Make this offer available to" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in trueOrFalse" :key="String(o.id)" :value="o.id">{{
                  o.text
                }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <!-- Tier picker, shown (and pre-populated) when the offer targets selected tiers.
               Without this the segments an offer already belongs to couldn't be seen or changed. -->
          <div v-if="activateOfferRequest.customerTierType === 'TIERED'" class="grid gap-1.5">
            <span class="text-sm font-medium">Select customer tiers</span>
            <p v-if="!customerSegments.length" class="text-sm text-muted-foreground">
              No customer tiers available.
            </p>
            <label
              v-for="(s, idx) in customerSegments"
              :key="idx"
              class="flex items-center gap-2 text-sm"
            >
              <Checkbox
                :model-value="selectedCustomerTiers.some((x) => x.id === s.id)"
                @update:model-value="
                  (v) => {
                    if (v) selectedCustomerTiers.push(s)
                    else selectedCustomerTiers = selectedCustomerTiers.filter((x) => x.id !== s.id)
                  }
                "
              />
              {{ s.text }}
            </label>
          </div>
        </div>
        <!-- grid gap separates the checkbox from the field below it, and the inner gap
             separates each field's label from its control. -->
        <div v-else class="grid gap-4">
          <label
            v-for="(s, idx) in giveAllCommission"
            :key="idx"
            class="flex items-center gap-2 text-sm"
          >
            <Checkbox
              :model-value="selectedCommissionType.some((x) => x.id === s.id)"
              @update:model-value="
                (v) => {
                  if (v) selectedCommissionType.push(s)
                  else selectedCommissionType = selectedCommissionType.filter((x) => x.id !== s.id)
                }
              "
            />
            {{ s.text }}
          </label>
          <div class="grid gap-1.5">
            <span class="text-sm">Make this offer available to</span>
            <Select v-model="activateOfferRequest.customerTierType">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Make this offer available to" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in trueOrFalse" :key="String(o.id)" :value="o.id">{{
                  o.text
                }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div v-if="activateOfferRequest.customerTierType === 'TIERED'" class="grid gap-1.5">
            <span class="text-sm font-medium">Select customer tiers</span>
            <p v-if="!customerSegments.length" class="text-sm text-muted-foreground">
              No customer tiers available.
            </p>
            <label
              v-for="(s, idx) in customerSegments"
              :key="idx"
              class="flex items-center gap-2 text-sm"
            >
              <Checkbox
                :model-value="selectedCustomerTiers.some((x) => x.id === s.id)"
                @update:model-value="
                  (v) => {
                    if (v) selectedCustomerTiers.push(s)
                    else selectedCustomerTiers = selectedCustomerTiers.filter((x) => x.id !== s.id)
                  }
                "
              />
              {{ s.text }}
            </label>
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel @click="showEditFormModal = false">Cancel</AlertDialogCancel>
          <AlertDialogAction
            :disabled="
              activateOfferRequest.splitOfCashbackPercentage.client > 100 ||
              activateOfferRequest.splitOfCashbackPercentage.client < 0 ||
              (activateOfferRequest.customerTierType === 'TIERED' && !selectedCustomerTiers.length)
            "
            @click="
              () => {
                showEditFormModal = false
                requestApproval()
              }
            "
            >Edit offer</AlertDialogAction
          >
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog
      v-if="openUploadModal"
      :open="openUploadModal"
      @update:open="(v) => (openUploadModal = v)"
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Add verified transactions</AlertDialogTitle>
        </AlertDialogHeader>
        <p class="text-sm text-muted-foreground">
          Upload the verified transaction csv to the system
        </p>
        <input
          id="rewardImgUpload"
          type="file"
          @change="uploadFile"
          ref="fileInput"
          accept=".csv"
        />
        <AlertDialogFooter>
          <AlertDialogCancel @click="openUploadModal = false">Cancel</AlertDialogCancel>
          <AlertDialogAction :disabled="uploadCSV == undefined" @click="uploadTransactions()"
            >Upload</AlertDialogAction
          >
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <!-- A right-side drawer rather than a centre modal: an offer can carry a lot of
         descriptors, and the drawer scrolls instead of overflowing the viewport. -->
    <Drawer
      direction="right"
      :open="viewEnrichmentStrings"
      @update:open="(o) => !o && (viewEnrichmentStrings = false)"
    >
      <DrawerContent class="sm:max-w-md">
        <DrawerHeader class="text-left">
          <DrawerTitle>Approved descriptors</DrawerTitle>
          <DrawerDescription
            >Boogi will match against these transaction descriptors</DrawerDescription
          >
        </DrawerHeader>
        <div class="flex flex-wrap gap-1.5 overflow-y-auto px-4 flex-1">
          <Badge
            v-for="(descriptor, id) in offer.unenrichedCreditorsStrings"
            :key="id"
            variant="secondary"
            class="font-mono"
            >{{ descriptor }}</Badge
          >
          <p
            v-if="!offer.unenrichedCreditorsStrings || !offer.unenrichedCreditorsStrings.length"
            class="text-sm text-muted-foreground"
          >
            No approved descriptors yet.
          </p>
        </div>
        <DrawerFooter class="flex-row justify-end gap-2">
          <Button variant="outline" @click="viewEnrichmentStrings = false">Close</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    <!-- Shown only for offers this publisher supplies, i.e. their own custom offers. -->
    <Alert v-if="isCustomOffer" variant="info" class="mb-4">
      <Info />
      <AlertTitle>You own this custom offer</AlertTitle>
      <AlertDescription>
        You created and fund this offer. Boogi handles matching transactions and responding with
        webhooks. It is your own responsibility to manage settlements.
      </AlertDescription>
    </Alert>

    <Card class="mb-4 overflow-hidden pt-0 gap-0">
      <div class="h-16"></div>
      <CardContent class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div class="flex items-end gap-4 min-w-0 -mt-10">
          <div
            class="size-20 shrink-0 rounded-xl border border-border bg-white shadow-sm flex items-center justify-center overflow-hidden"
          >
            <img
              class="max-h-full max-w-full object-contain"
              :src="offer.displayInformation.logo"
            />
          </div>
          <div class="min-w-0 pb-0.5">
            <p class="text-xl font-semibold tracking-tight truncate">
              {{ offer.retailer.retailerName }}
            </p>
            <div
              class="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 items-center text-xs text-muted-foreground"
            >
              <CopyId :value="offer.id" :max-width="220" />
            </div>

            <div class="mt-3 flex flex-wrap gap-2 items-center">
              <template v-if="activatedOffer != undefined">
                <span
                  class="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap"
                >
                  <component
                    :is="approvalIcon(activatedOffer.offerApproval)"
                    :class="[
                      'size-3.5',
                      activatedOffer.offerApproval === 'APPROVED'
                        ? 'text-green-500 dark:text-green-400'
                        : '',
                      activatedOffer.offerApproval === 'DECLINED'
                        ? 'text-red-500 dark:text-red-400'
                        : '',
                      activatedOffer.offerApproval === 'PENDING' ? 'animate-spin' : '',
                    ]"
                  />
                  {{ approvalLabel(activatedOffer.offerApproval) }}
                </span>
                <span
                  class="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap"
                >
                  <component
                    :is="activeIcon(activatedOffer.active)"
                    :class="[
                      'size-3.5',
                      activatedOffer.active ? 'text-green-500 dark:text-green-400' : '',
                    ]"
                  />
                  {{ activatedOffer.active ? 'Live' : 'Deactivated' }}
                </span>
              </template>
              <Badge variant="outline">{{ formatOfferType(offer.offerType) }}</Badge>
              <Badge variant="outline">{{ offer.country }}</Badge>
            </div>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2 lg:justify-end shrink-0">
          <Button
            v-if="offer.supplier == publisherId"
            size="sm"
            variant="outline"
            @click="$router.push('/edit-offer/' + offerId)"
          >
            <Pencil class="w-4 h-4 mr-1" /> Edit offer
          </Button>
          <Button
            v-if="offer.supplier == publisherId"
            size="sm"
            variant="destructive"
            @click="deleteBYOOOffer()"
          >
            <Trash2 class="w-4 h-4 mr-1" /> Delete offer
          </Button>

          <Button v-if="activatedOffer == undefined" size="sm" @click="showFormModal = true"
            >Request approval</Button
          >

          <!-- A declined offer can never go live, so activate / deactivate / edit are not
               offered for it; removing it from the program is still allowed. -->
          <Button
            v-if="activatedOffer != undefined && !activatedOffer.active && !offerDeclined"
            size="sm"
            @click="
              askConfirm(
                'Activate offer',
                'This offer will become live and available to your customers.',
                () => activateOffer(activatedOffer.id)
              )
            "
            >Activate</Button
          >

          <Button
            v-if="activatedOffer != undefined && activatedOffer.active && !offerDeclined"
            size="sm"
            variant="outline"
            @click="
              askConfirm(
                'Deactivate offer',
                'This offer will stop being available to your customers.',
                () => deActivateOffer(activatedOffer.id)
              )
            "
            >Deactivate</Button
          >

          <Button
            v-if="activatedOffer != undefined && !offerDeclined"
            size="sm"
            variant="outline"
            @click="handleEditOffer()"
            >Edit</Button
          >

          <p v-if="offerDeclined" class="text-sm text-muted-foreground self-center">
            This offer was declined, so it cannot be activated or edited.
          </p>

          <Button
            v-if="offer.ownerId == 'BOOGI' && activatedOffer != undefined"
            size="sm"
            variant="destructive"
            @click="
              askConfirm(
                'Remove offer from program',
                'This removes the offer from your program. You can request it again later.',
                () => deleteOffer()
              )
            "
          >
            <Trash2 class="w-4 h-4 mr-1" /> Remove
          </Button>
        </div>
      </CardContent>
    </Card>

    <section v-if="offer != null" class="mb-4">
      <div
        class="grid grid-cols-1 gap-4 *:data-[slot=card]:shadow-xs @xl/main:grid-cols-2 @5xl/main:grid-cols-3 md:grid-cols-3 dark:*:data-[slot=card]:bg-card"
      >
        <Card class="@container/card">
          <CardHeader>
            <CardDescription>Total cashback</CardDescription>
            <CardTitle class="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
              {{ headlineCashback('maxAvailableValue') }}
            </CardTitle>
            <p v-if="maxCapLabel()" class="text-xs text-muted-foreground">{{ maxCapLabel() }}</p>
            <CardAction>
              <Banknote class="size-5 text-muted-foreground" />
            </CardAction>
          </CardHeader>
        </Card>

        <Card class="@container/card">
          <CardHeader>
            <CardDescription>User cashback</CardDescription>
            <CardTitle class="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
              {{ headlineCashback('endUserValue') }}
            </CardTitle>
            <CardAction>
              <Users class="size-5 text-muted-foreground" />
            </CardAction>
          </CardHeader>
        </Card>

        <Card class="@container/card">
          <CardHeader>
            <CardDescription>Your commission</CardDescription>
            <CardTitle class="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
              {{ headlineCashback('clientValue') }}
            </CardTitle>
            <CardAction>
              <HandCoins class="size-5 text-muted-foreground" />
            </CardAction>
          </CardHeader>
        </Card>
      </div>
    </section>

    <Tabs v-model="tab" class="mb-4">
      <TabsList class="mb-4 flex-wrap">
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="audience">Selected audience</TabsTrigger>
        <TabsTrigger value="condition">Offer conditions</TabsTrigger>
        <TabsTrigger value="matching">Matching criteria</TabsTrigger>
        <TabsTrigger value="ledger">Cashback ledger</TabsTrigger>
        <TabsTrigger value="display">Display information</TabsTrigger>
        <TabsTrigger v-if="offer.supplier == publisherId" value="payout">Payouts</TabsTrigger>
      </TabsList>

      <TabsContent value="overview">
        <Card>
          <CardHeader>
            <CardTitle>Overview</CardTitle>
            <CardDescription>Overview of key offer information</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              v-if="offer"
              :headers="activatedOffersHeaders"
              :items="[offer]"
              :loading="loading"
              :error="error"
              @retry="getOffer()"
              :hide-footer="true"
            >
              <template #item-country="retailers">
                <Badge variant="secondary" v-if="retailers.country">{{ retailers.country }}</Badge>
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-supplier="retailers">
                <div v-if="retailers.supplier" class="flex items-center gap-2 whitespace-nowrap">
                  <img
                    v-if="supplierLogo(retailers.supplier)"
                    :src="supplierLogo(retailers.supplier)"
                    :alt="supplierName(retailers.supplier)"
                    class="h-5 w-5 rounded object-contain shrink-0"
                  />
                  <span class="text-sm">{{ supplierName(retailers.supplier) }}</span>
                </div>
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-currencyList="retailers">
                <div
                  v-if="retailers.currencyList && retailers.currencyList.length"
                  class="flex flex-wrap gap-1"
                >
                  <Badge v-for="c in retailers.currencyList" :key="c" variant="secondary">{{
                    c
                  }}</Badge>
                </div>
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-offertype="retailers">
                <Badge variant="secondary">{{ formatOfferType(retailers.offerType) }}</Badge>
              </template>
              <template #item-active="">
                <div v-if="activatedOffer">
                  <Badge variant="success" v-if="activatedOffer.active">Live</Badge>
                  <Badge variant="warning" v-else>Deactivated</Badge>
                </div>
                <Badge variant="warning" v-else>Deactivated</Badge>
              </template>
              <template #item-spendingCap="retailers">
                <span
                  v-if="retailers.spendingCapped || retailers.spendingCapAmount > 0"
                  class="text-sm tabular-nums whitespace-nowrap"
                  >{{
                    getCurrencySymbol(retailers.currencyList) + retailers.spendingCapAmount
                  }}</span
                >
                <Badge v-else variant="secondary">No cashback cap applied</Badge>
              </template>
              <template #item-offerApproval="">
                <div v-if="activatedOffer">
                  <Badge variant="success" v-if="activatedOffer.offerApproval === 'APPROVED'"
                    >Approved</Badge
                  >
                  <Badge variant="warning" v-if="activatedOffer.offerApproval === 'PENDING'"
                    >Pending approval</Badge
                  >
                  <Badge variant="destructive" v-if="activatedOffer.offerApproval === 'DECLINED'"
                    >Approval declined</Badge
                  >
                </div>
                <Badge variant="secondary" v-else>No approval requested</Badge>
              </template>
            </DataTable>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="audience">
        <Card>
          <CardHeader>
            <CardTitle>Audience information</CardTitle>
            <CardDescription>Who this offer is avaliable for</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              :headers="audienceOfferHeaders"
              :items="activatedOfferData"
              :loading="loading"
              :error="error"
              @retry="getOffer()"
              :hide-footer="true"
            >
              <template #item-audience="val">
                <Badge variant="secondary" v-if="val.customerTierType == 'DEFAULT'"
                  >All users</Badge
                >
                <Badge variant="secondary" v-if="val.customerTierType == 'TIERED'"
                  >Targeted segments</Badge
                >
                <Badge variant="secondary" v-if="val.customerTierType == 'SET'"
                  >Targeted users</Badge
                >
              </template>

              <!-- Only a TIERED offer has segments; anything else left this cell blank, which
               read as missing data rather than "no segments apply". -->
              <template #item-segment="val">
                <div v-if="val.customerTierType == 'TIERED' && selectedSegments.length">
                  <Badge
                    variant="secondary"
                    class="mr-1"
                    v-for="(tier, id) in selectedSegments"
                    :key="id"
                    >{{ tier }}</Badge
                  >
                </div>
                <span
                  v-else-if="val.customerTierType == 'TIERED'"
                  class="text-sm text-muted-foreground"
                >
                  No segments selected
                </span>
                <span v-else class="text-sm text-muted-foreground">Available to all users</span>
              </template>
            </DataTable>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="matching" class="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Matching criteria</CardTitle>
            <CardDescription>How Boogi will match transactions to this offer</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              :headers="offersMatchingHeaders"
              :items="[offer]"
              :loading="loading"
              :error="error"
              @retry="getOffer()"
              :hide-footer="true"
            >
              <template #item-currencyList="row">
                <div
                  v-if="row.currencyList && row.currencyList.length"
                  class="flex flex-wrap gap-1"
                >
                  <Badge v-for="c in row.currencyList" :key="c" variant="secondary">{{ c }}</Badge>
                </div>
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <!-- Styled to match the currency chips alongside it. -->
              <template #item-validFor="row">
                <Badge variant="secondary" v-if="row.validFor">{{ row.validFor }}</Badge>
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-superMatch="row">
                <Badge :variant="row.superMatch ? 'success' : 'secondary'">{{
                  row.superMatch ? 'Yes' : 'No'
                }}</Badge>
              </template>
              <!-- `text` is not a Button prop, so this rendered as an empty black button. -->
              <template #item-enrichedString="">
                <Button variant="outline" size="sm" @click="viewEnrichmentStrings = true"
                  >View descriptors</Button
                >
              </template>
            </DataTable>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Channels</CardTitle>
            <CardDescription>View the channels for this offer</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              :headers="headersTable4"
              :items="offer.channels"
              :loading="loading"
              :error="error"
              @retry="getOffer()"
              :hide-footer="true"
            >
              <template #item-mids="row">
                <div v-if="row.mids && row.mids.length" class="flex flex-wrap gap-1">
                  <Badge v-for="mid in row.mids" :key="mid" variant="secondary" class="font-mono">{{
                    mid
                  }}</Badge>
                </div>
                <span v-else class="text-muted-foreground">—</span>
              </template>
            </DataTable>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="condition">
        <Card>
          <CardHeader>
            <CardTitle>Offer conditions</CardTitle>
            <CardDescription>View breakdown of earnings by conditions</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              :headers="headersTable1"
              :items="offer.cashbackRules"
              :loading="loading"
              :error="error"
              @retry="getOffer()"
              :hide-footer="true"
            >
              <template #item-usetype="usetype">
                <Badge variant="secondary">{{ usetype.offerUsageType }}</Badge>
              </template>
              <!-- Slot name must match the header's `value` (customerType) exactly, otherwise the
               table falls back to printing the raw value instead of rendering this chip. -->
              <template #item-customerType="customerType">
                <Badge variant="secondary" v-if="customerType.customerType == 'EXISTING_CUSTOMER'"
                  >ALL_CUSTOMERS</Badge
                >
                <Badge variant="secondary" v-else>{{ customerType.customerType }}</Badge>
              </template>
              <template #item-fixedDistributorCommission="val">
                <div v-if="val.conditions.length === 1" class="">
                  <Badge variant="success" v-if="val.conditions[0].fixedDistributorCommission"
                    >True</Badge
                  >
                  <Badge variant="destructive" v-else>False</Badge>
                </div>
              </template>
              <template #item-userCashback="val">
                <span class="text-sm tabular-nums whitespace-nowrap"
                  >{{
                    cashbackPrefix(
                      val.conditions[0].value.type,
                      getCurrencySymbol(this.offer.currencyList)
                    )
                  }}{{
                    val.conditions[0].value.endUserValue == null
                      ? 'Please set'
                      : val.conditions[0].value.endUserValue.toFixed(2)
                  }}{{ cashbackSuffix(val.conditions[0].value.type) }}</span
                >
              </template>
              <template #item-totalCashback="val">
                <div v-if="val.conditions.length === 1" class="flex items-baseline gap-1.5">
                  <span class="text-sm tabular-nums whitespace-nowrap"
                    >{{
                      cashbackPrefix(
                        val.conditions[0].value.type,
                        getCurrencySymbol(this.offer.currencyList)
                      )
                    }}{{ val.conditions[0].value.maxAvailableValue.toFixed(2)
                    }}{{ cashbackSuffix(val.conditions[0].value.type) }}</span
                  >
                  <!-- Surface the per-transaction £ cap for PERCENTAGE_MAX offers. -->
                  <span
                    v-if="
                      val.conditions[0].value.type === 'PERCENTAGE_MAX' &&
                      val.conditions[0].value.maximum > 0
                    "
                    class="text-xs text-muted-foreground whitespace-nowrap"
                    >up to {{ getCurrencySymbol(offer.currencyList)
                    }}{{ val.conditions[0].value.maximum.toFixed(2) }}</span
                  >
                </div>
              </template>
              <template #item-publisherCashback="val">
                <span class="text-sm tabular-nums whitespace-nowrap"
                  >{{
                    cashbackPrefix(
                      val.conditions[0].value.type,
                      getCurrencySymbol(this.offer.currencyList)
                    )
                  }}{{
                    val.conditions[0].value.clientValue == null
                      ? 'Please set'
                      : val.conditions[0].value.clientValue.toFixed(2)
                  }}{{ cashbackSuffix(val.conditions[0].value.type) }}</span
                >
              </template>
              <template #item-cashbackType="val">
                <Badge variant="secondary" v-if="val.conditions.length === 1">{{
                  val.conditions[0].value.type
                }}</Badge>
              </template>
              <template #item-conditions="val">
                <div v-if="val.conditions.length === 1" class="">
                  <!-- secondary to match the other chips in this table (outline read as a different style) -->
                  <Badge variant="secondary" class="text-xs whitespace-nowrap">
                    <span v-if="val.conditions[0].type === 'GREATER_THAN'">
                      Greater than {{ getCurrencySymbol(this.offer.currencyList) }}
                    </span>
                    <span v-if="val.conditions[0].type === 'LESS_THAN'">
                      Less than {{ getCurrencySymbol(this.offer.currencyList) }}
                    </span>
                    <span v-if="val.conditions[0].type === 'BETWEEN'">
                      Between {{ getCurrencySymbol(this.offer.currencyList) }}
                    </span>
                    {{ val.conditions[0].lowerBound }}
                    <span v-if="val.conditions[0].type === 'BETWEEN'">
                      and {{ getCurrencySymbol(this.offer.currencyList) }}
                    </span>
                    {{ val.conditions[0].upperBound }}
                  </Badge>
                </div>
              </template>
            </DataTable>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="ledger">
        <Card>
          <CardHeader>
            <CardTitle>Cashback ledger</CardTitle>
            <CardDescription>View cashback activity for this offer</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              :headers="cashbackTable"
              :items="cashbackObjects"
              :loading="loadCashback"
              :error="loadCashbackError"
              @retry="getCashbackObjects(serverOptions.page - 1, serverOptions.rowsPerPage)"
              :filter-groups="statusFilterGroups"
              :filters="statusFilters"
              @filter-change="onStatusFilterChange"
              v-model:server-options="serverOptions"
              :server-items-length="serverItemsLength"
            >
              <template #item-id="id">
                <CopyId :value="id.id" :max-width="120" />
              </template>
              <template #item-createdOn="createdOn">
                <span class="text-xs whitespace-nowrap">{{
                  niceDateTime(createdOn.createdOn)
                }}</span>
              </template>
              <template #item-status="status">
                <div class="flex items-center justify-between">
                  <div>
                    <Badge variant="success" v-if="status.status === 'PAID'">{{
                      status.status
                    }}</Badge>
                    <Badge variant="destructive" v-else-if="status.status === 'DECLINED'">{{
                      status.status
                    }}</Badge>
                    <Badge variant="warning" v-else>{{ status.status }}</Badge>
                  </div>
                </div>
              </template>
              <!-- Matches the main cashback ledger: the user id links through to the user. -->
              <template #item-internalEndUserId="internalEndUserId">
                <router-link
                  v-if="internalEndUserId.endUserId"
                  :to="`/enrolled-users/users/${internalEndUserId.endUserId}`"
                  class="text-xs font-mono font-medium whitespace-nowrap text-sky-600 dark:text-sky-400 hover:text-sky-500 dark:hover:text-sky-300 hover:underline underline-offset-2 transition-colors"
                  @click.stop
                >
                  {{ internalEndUserId.endUserId }}
                </router-link>
                <p v-else class="text-xs font-mono whitespace-nowrap">—</p>
              </template>
              <template #item-internalTransactionId="internalEndUserId">
                <p class="text-xs font-mono whitespace-nowrap">
                  {{ internalEndUserId.transactionId || '—' }}
                </p>
              </template>
              <template #item-retailer="cashback">
                <div
                  v-if="cashback.programOffer && cashback.programOffer.retailer"
                  class="flex items-center gap-2.5"
                >
                  <img
                    v-if="cashback.programOffer.retailer.logo"
                    :src="cashback.programOffer.retailer.logo"
                    class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white"
                    alt=""
                  />
                  <span
                    class="text-sm font-medium truncate"
                    :title="cashback.programOffer.retailer.retailerName"
                    >{{ cashback.programOffer.retailer.retailerName }}</span
                  >
                </div>
                <div v-else class="flex items-center gap-2.5">
                  <img
                    src="cardlyticsIcon.png"
                    class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white"
                    alt=""
                  />
                  <span class="text-sm font-medium whitespace-nowrap">Cardlytics</span>
                </div>
              </template>
              <template #item-transactionValue="cashback">
                <span
                  v-if="cashback.transactionAmount != null"
                  class="text-sm tabular-nums whitespace-nowrap"
                  >{{ cashback.transactionAmount.currency
                  }}{{
                    (Math.round(cashback.transactionAmount.amount * 100) / 100).toFixed(2)
                  }}</span
                >
                <span v-else class="text-sm text-muted-foreground whitespace-nowrap"
                  >Affiliate Transaction</span
                >
              </template>
              <template #item-userReceivable="cashback">
                <span
                  v-if="cashback.payoutData != null"
                  class="text-sm tabular-nums whitespace-nowrap"
                  >{{ cashback.payoutData.currency
                  }}{{
                    (Math.round(cashback.payoutData.endUserCashbackReceivable * 100) / 100).toFixed(
                      2
                    )
                  }}</span
                >
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-publisherReceivable="cashback">
                <span
                  v-if="cashback.payoutData != null"
                  class="text-sm tabular-nums whitespace-nowrap"
                  >{{ cashback.payoutData.currency
                  }}{{
                    (
                      Math.round(cashback.payoutData.publisherCashbackReceivable * 100) / 100
                    ).toFixed(2)
                  }}</span
                >
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-declinedReason="cashback">
                <Badge variant="destructive" v-if="cashback.declinedReason">{{
                  cashback.declinedReason
                }}</Badge>
              </template>
            </DataTable>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="payout">
        <div v-if="offer.supplier == publisherId">
          <Card>
            <CardHeader class="flex flex-row items-center justify-between">
              <CardTitle>Cashback payouts</CardTitle>
              <Button
                size="sm"
                class="w-48"
                @click="
                  () => {
                    openUploadModal = true
                    verification = verificationObject
                  }
                "
                >Submit transactions</Button
              >
            </CardHeader>
            <CardContent>
              <DataTable
                :headers="settlementsHeader"
                :items="receivables"
                :loading="loading"
                :error="error"
                @retry="
                  getTransactionVerifications(serverOptions2.page - 1, serverOptions2.rowsPerPage)
                "
                v-model:server-options="serverOptions2"
                :server-items-length="serverItemsLength2"
              >
                <template #item-createdOn="{ createdOn }">
                  <span class="text-sm text-muted-foreground whitespace-nowrap">{{
                    niceDateTime(createdOn)
                  }}</span>
                </template>

                <template #item-action="verificationObject">
                  <div class="flex justify-end items-center gap-1">
                    <Button variant="outline" size="sm" @click="download(verificationObject)"
                      >Download CSV</Button
                    >
                  </div>
                </template>
              </DataTable>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      <TabsContent value="display">
        <div class="grid grid-cols-3 gap-5">
          <div class="w-full max-w-md col-span-full xl:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Display information</CardTitle>
                <CardDescription>Preview content from our offer API:</CardDescription>
              </CardHeader>
              <CardContent>
                <div class="rounded-xl bg-muted/40 pt-3 pb-4">
                  <!--name-->
                  <!--header-->
                  <div class="px-2 grid gap-2 rounded-xl">
                    <div
                      class="flex justify-start items-center px-1 gap-2 w-full h-15 bg-card text-card-foreground shadow rounded-xl border border-border"
                    >
                      <img
                        class="object-contain w-12.5 h-12.5 rounded-lg border border-border bg-white"
                        :src="offer.displayInformation.logo"
                      />
                      <!-- Retailer leads, offer name reads as its subtitle. -->
                      <div class="min-w-0">
                        <p class="font-semibold truncate">{{ offer.retailer.retailerName }}</p>
                        <p class="text-xs text-muted-foreground truncate">{{ offer.offerName }}</p>
                      </div>
                    </div>
                    <div
                      class="p-3 gap-2 w-full bg-card text-card-foreground shadow rounded-lg flex justify-between items-center"
                    >
                      <div>
                        <p class="text-sm font-medium">Cashback:</p>
                      </div>
                      <!-- Borderless and slightly larger so the value reads as a figure rather
                       than a cramped outlined chip. -->
                      <div class="inline">
                        <Badge
                          variant="secondary"
                          class="border-0 text-base font-bold px-3 py-1 whitespace-nowrap"
                        >
                          {{ displayCashback }}
                        </Badge>
                      </div>
                    </div>
                    <div class="p-3 gap-2 w-full bg-card text-card-foreground shadow rounded-lg">
                      <p class="text-sm font-medium mb-1">Description</p>
                      <p v-if="sanitizedDescription" class="text-sm">{{ sanitizedDescription }}</p>
                      <p v-else class="text-sm text-muted-foreground">
                        No description set for this offer.
                      </p>
                    </div>
                    <a
                      v-if="safeOfferLink"
                      class="block w-full"
                      :href="safeOfferLink"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button class="w-full">Visit website</Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
  Button,
  Badge,
  Input,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  CopyId,
  Alert,
  AlertTitle,
  AlertDescription,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@boogi/ui'
import {
  Banknote,
  Users,
  HandCoins,
  Trash2,
  Pencil,
  CircleCheck,
  Ban,
  Loader,
  CircleDashed,
  Info,
} from 'lucide-vue-next'
import { DateTime } from 'luxon'
import { breadcrumbState } from '@/stores/breadcrumb'
import { DataTable } from '@boogi/ui'
import { toast } from '@boogi/ui'

export default {
  name: 'offer-page',
  components: {
    DataTable,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    Button,
    Badge,
    Input,
    Checkbox,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
    CopyId,
    Alert,
    AlertTitle,
    AlertDescription,
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
    DrawerFooter,
    Banknote,
    Users,
    HandCoins,
    Trash2,
    Pencil,
    CircleCheck,
    Ban,
    Loader,
    CircleDashed,
    Info,
  },
  props: {},

  data() {
    return {
      tab: 'overview',
      confirmModal: { open: false, title: '', description: '', action: null },
      cashbackObjects: [],
      publisherId: localStorage.getItem('publisherId'),
      showEditFormModal: false,
      viewEnrichmentStrings: false,

      // 'SET' (Selected users) is deliberately not offered any more — the feature is being
      // removed. Existing offers already on SET still render correctly wherever
      // customerTierType is displayed; it just can't be chosen going forward.
      trueOrFalse: [
        { id: 'DEFAULT', text: 'All users' },
        { id: 'TIERED', text: 'Selected tiers' },
      ],

      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,

      userSearch: {
        userId: '',
      },
      loading: true,
      error: false,
      cashbackTable: [
        { text: 'Id', value: 'id' },
        { text: 'Date created', value: 'createdOn' },
        { text: 'Status', value: 'status' },
        { text: 'User id', value: 'internalEndUserId' },
        { text: 'Transaction id', value: 'internalTransactionId' },

        { text: 'Retailer', value: 'retailer' },
        { text: 'Transaction value', value: 'transactionValue' },
        { text: 'End user receivable', value: 'userReceivable' },
        { text: 'Publisher receivable', value: 'publisherReceivable' },
        { text: 'Declined reason', value: 'declinedReason' },
      ],

      customerSegments: [],

      selectedCommissionType: [],

      giveAllCommission: [{ id: -1, text: 'Give all commission' }],

      openUploadModal: false,

      settlementsHeader: [
        { text: 'Created on', value: 'createdOn' },
        { text: 'Download CSV', value: 'action' },
      ],
      receivables: [],

      selectedCustomerTiers: [],

      activateOfferRequest: {
        splitOfCashbackPercentage: {
          endUser: 50,
          client: 50,
        },
        activate: true,
        customerTierType: 'DEFAULT',
        selectedCustomerTiers: [],
      },

      headersTable4: [
        { text: 'Channel name', value: 'channelName' },
        { text: 'Address', value: 'address' },
        { text: 'City', value: 'city' },
        { text: 'Postcode', value: 'postcode' },
        { text: 'Channel type', value: 'channelType' },
        { text: 'Mids', value: 'mids' },
      ],

      declineOffer: { reason: '' },
      selectedOffer: '',
      pecentageNotRight: '',
      successAction: '',

      retailerId: this.$route.params.retailerId,
      offerId: this.$route.params.offerId,
      offer: undefined,
      activatedOffer: undefined,
      activatedOfferData: [],
      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      declineModal: false,

      // Supplier code -> display name / logo. Only LUX has a bundled logo today; anything
      // else falls back to its code until we have a real supplier feed with logos.
      suppliers: {
        LUX: { name: 'Lux', logo: require('@/assets/lux_logo.jpeg') },
        TRADEDOUBLER: { name: 'Tradedoubler', logo: '' },
        AWIN: { name: 'Awin', logo: '' },
      },

      headersTable1: [
        { text: 'Customer type', value: 'customerType' },
        { text: 'Cashback type', value: 'cashbackType' },
        { text: 'Use type', value: 'usetype' },
        { text: 'Total cashback', value: 'totalCashback' },
        { text: 'User cashback', value: 'userCashback' },
        { text: 'Your commission', value: 'publisherCashback' },
        { text: 'Spend condition', value: 'conditions' },
      ],

      activatedOffersHeaders: [
        { text: 'Approval status', value: 'offerApproval' },
        { text: 'Availability status', value: 'active' },
        { text: 'Country', value: 'country' },
        { text: 'Accepted currencies', value: 'currencyList' },
        { text: 'Offer type', value: 'offertype' },
        { text: 'End user cashback cap', value: 'spendingCap' },
        { text: 'Supplier', value: 'supplier' },
      ],

      audienceOfferHeaders: [
        { text: 'Avaliable for', value: 'audience', width: '20' },
        { text: 'Selected segments', value: 'segment', width: '80' },
      ],

      offersMatchingHeaders: [
        { text: 'Match against', value: 'validFor' },
        { text: 'Accepted currencies', value: 'currencyList' },
        { text: 'Requires additional descriptor matching', value: 'superMatch' },
        { text: 'Approved descriptors', value: 'enrichedString' },
      ],

      cashbackStatuses: [
        { id: 'BEING_VERIFIED', text: 'BEING_VERIFIED' },
        { id: 'CREATED', text: 'CREATED' },
        { id: 'DECLINED', text: 'DECLINED' },
        { id: 'DISQUALIFIED', text: 'DISQUALIFIED' },
        { id: 'PAID', text: 'PAID' },
        { id: 'RECEIVED', text: 'RECEIVED' },
        { id: 'VERIFIED', text: 'VERIFIED' },
      ],

      selectStatus: [
        { id: 'BEING_VERIFIED', text: 'BEING_VERIFIED' },
        { id: 'CREATED', text: 'CREATED' },
        { id: 'DECLINED', text: 'DECLINED' },
        { id: 'PAID', text: 'PAID' },
        { id: 'RECEIVED', text: 'RECEIVED' },
        { id: 'REFUNDED', text: 'REFUNDED' },
        { id: 'VERIFIED', text: 'VERIFIED' },
      ],

      resetStatus: [
        { id: 'BEING_VERIFIED', text: 'BEING_VERIFIED' },
        { id: 'CREATED', text: 'CREATED' },
        { id: 'DECLINED', text: 'DECLINED' },
        { id: 'PAID', text: 'PAID' },
        { id: 'RECEIVED', text: 'RECEIVED' },
        { id: 'REFUNDED', text: 'REFUNDED' },
        { id: 'VERIFIED', text: 'VERIFIED' },
      ],

      statuses: [
        { id: 'BEING_VERIFIED', text: 'BEING_VERIFIED' },
        { id: 'CREATED', text: 'CREATED' },
        { id: 'DECLINED', text: 'DECLINED' },
        { id: 'DISQUALIFIED', text: 'DISQUALIFIED' },
        { id: 'PAID', text: 'PAID' },
        { id: 'RECEIVED', text: 'RECEIVED' },
        { id: 'REFUNDED', text: 'REFUNDED' },
        { id: 'VERIFIED', text: 'VERIFIED' },
      ],

      offers: [],
      loadCashback: true,
      loadCashbackError: false,
    }
  },
  created() {
    this.getOffer()
    this.getCustomerTiers()
  },
  unmounted() {
    breadcrumbState.clear()
  },
  methods: {
    // Open the shared confirmation modal; `action` runs on confirm.
    askConfirm(title, description, action) {
      this.confirmModal = { open: true, title, description, action }
    },
    confirmProceed() {
      const action = this.confirmModal.action
      this.confirmModal.open = false
      if (typeof action === 'function') action()
    },
    setBreadcrumb() {
      breadcrumbState.set([
        { label: 'Merchant catalogue', to: '/offer-catalogue' },
        { label: this.offer.retailer.retailerName, to: `/retailer/${this.retailerId}` },
        { label: 'Offer' },
      ])
    },

    approvalLabel(status) {
      if (status === 'APPROVED') return 'Approved'
      if (status === 'DECLINED') return 'Declined'
      if (status === 'PENDING') return 'Pending'
      return 'Not applied for'
    },

    approvalIcon(status) {
      if (status === 'APPROVED') return CircleCheck
      if (status === 'DECLINED') return Ban
      if (status === 'PENDING') return Loader
      return CircleDashed
    },

    activeIcon(active) {
      return active ? CircleCheck : CircleDashed
    },

    formatOfferType(type) {
      if (type === 'PAYMENT') return 'Payment-linked'
      if (type === 'ONLINE') return 'Affiliate links'
      if (type === 'CLICK_TO_ACTIVATE') return 'Click to activate'
      return type
    },
    // Formats one of the three headline figures. Percentage offers render as "2.00%",
    // fixed-amount offers as "£10.00". These used to be built inline with
    // `type == 'PERCENTAGE' || 'PERCENTAGE_MAX'`, which is always truthy (it parses as
    // `(type == 'PERCENTAGE') || 'PERCENTAGE_MAX'`), so a fixed £ amount was always
    // rendered as a percentage. Reuses the same prefix/suffix helpers as the table below.
    headlineCashback(field) {
      const value =
        this.getBestCashbackRule &&
        this.getBestCashbackRule.rule &&
        this.getBestCashbackRule.rule.conditions &&
        this.getBestCashbackRule.rule.conditions[0] &&
        this.getBestCashbackRule.rule.conditions[0].value
      if (!value) return '—'
      const amount = value[field]
      if (amount == null) return 'Please set'
      const currency = this.getCurrencySymbol(this.offer.currencyList)
      return `${this.cashbackPrefix(value.type, currency)}${Number(amount).toFixed(2)}${this.cashbackSuffix(value.type)}`
    },

    // A PERCENTAGE_MAX offer ("X% up to £Y") caps each transaction's cashback at
    // value.maximum. The percentage figures alone don't show that ceiling, so surface it.
    maxCapLabel() {
      const value =
        this.getBestCashbackRule &&
        this.getBestCashbackRule.rule &&
        this.getBestCashbackRule.rule.conditions &&
        this.getBestCashbackRule.rule.conditions[0] &&
        this.getBestCashbackRule.rule.conditions[0].value
      if (!value || value.type !== 'PERCENTAGE_MAX' || !(value.maximum > 0)) return ''
      return `up to ${this.getCurrencySymbol(this.offer.currencyList)}${Number(value.maximum).toFixed(2)} per transaction`
    },

    // Suppliers are referenced by short code (LUX, TRADEDOUBLER, ...). Show a friendly
    // name, and a logo where we have one bundled.
    supplierName(code) {
      const supplier = this.suppliers[String(code).toUpperCase()]
      return (supplier && supplier.name) || code
    },
    supplierLogo(code) {
      const supplier = this.suppliers[String(code).toUpperCase()]
      return (supplier && supplier.logo) || ''
    },

    cashbackPrefix(type, currency) {
      if (type === 'PERCENTAGE' || type === 'PERCENTAGE_MAX') return ''
      return currency || ''
    },
    cashbackSuffix(type) {
      if (type === 'PERCENTAGE' || type === 'PERCENTAGE_MAX') return '%'
      return ''
    },

    getCurrencySymbol(currencyCode, locale = 'en-US') {
      return (0)
        .toLocaleString(locale, {
          style: 'currency',
          currency: currencyCode[0],
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
        .replace(/\d/g, '')
        .trim() // strip digits, leave symbol
    },

    // Native DataTable filter (matches the standalone Cashback ledger page) — replaces
    // the old bespoke filter modal.
    onStatusFilterChange(updated) {
      const ids = (updated && updated.status) || []
      this.selectStatus = this.statuses.filter((s) => ids.includes(s.id))
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getCashbackObjects(0, this.serverOptions.rowsPerPage)
    },

    async getCashbackObjects(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
      this.loadCashback = true
      this.loadCashbackError = false

      if (this.selectStatus.length > 0) {
        var selectStatus = []
        this.selectStatus.forEach((element) => {
          selectStatus.push(element.id)
        })
      }

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/cashback-ledger/program-offer/${this.offer.id}?page=${page}&size=${size}&status=${selectStatus}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.cashbackObjects = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.loadCashbackError = true
        }
      } catch (e) {
        this.loadCashbackError = true
      } finally {
        this.loadCashback = false
      }
    },

    handleEditOffer() {
      this.showEditFormModal = true
      this.activateOfferRequest.customerTierType = this.activatedOffer.customerTierType
      this.selectedCustomerTiers = this.customerSegments.filter((segment) =>
        this.activatedOffer.selectedCustomerTiers.includes(segment.id)
      )
    },

    uploadFile: function () {
      // const file = event.target.files[0];
      this.uploadCSV = event.target.files[0]
    },

    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATE_MED)
    },

    async getCustomerTiers() {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      // Segments are only needed to populate the approval-modal dropdown. A
      // failure here must NOT flip the shared loading/error flags, or it breaks
      // every offer tab that binds them.
      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/customer-tier/for-offers`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.customerSegments = (Array.isArray(object) ? object : []).map((tier) => ({
            id: tier.id,
            text: tier.name,
          }))
        }
      } catch (e) {
        /* ignore — segments are optional context */
      }
    },

    async getTransactionVerifications(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/bring-your-own-offer-fulfillment/program-offer/${this.activatedOffer.id}?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.serverItemsLength = object.totalElements
          this.receivables = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async uploadTransactions() {
      const formData = new FormData()
      formData.append('multipartFile', this.uploadCSV)

      try {
        const token = localStorage.getItem('token')
        this.loading = true

        const response = await this.$apiFetch(
          `${localStorage.getItem('apiUrl')}/private/api/client/bring-your-own-offer-fulfillment/upload`,
          {
            method: 'POST',
            body: formData,
            headers: {
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.openUploadModal = false
        }
      } catch (error) {
        return
      }
    },

    async activateOffer(offerId) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/offer/activate/${offerId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.successAction = 'Offer Activated'
          this.getOffer()
          this.showSuccessModal = true
        }
      } catch {
        return
      }
    },

    async deActivateOffer(offerId) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/offer/deactivate/${offerId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.getOffer()
          this.successAction = 'Offer deactivated'
          this.showSuccessModal = true
        }
      } catch {
        return
      }
    },

    async requestApproval() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        this.loading = true

        this.activateOfferRequest.selectedCustomerTiers = this.selectedCustomerTiers.map(
          (val) => val.id
        )

        if (this.selectedCommissionType.length > 0) {
          this.activateOfferRequest.splitOfCashbackPercentage.client = -1
        }

        console.log(this.activateOfferRequest)

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/offer/apply/${this.offerId}`,
          {
            method: 'POST',
            body: JSON.stringify(this.activateOfferRequest),
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.loading = false
          this.getOffer()
        }
      } catch (error) {
        console.log(error)
        return
      }
    },

    async download(transactionVerification) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/bring-your-own-offer-fulfillment/${transactionVerification.id}/download`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          const blob = await response.blob() // Convert the response to a blob
          const url = window.URL.createObjectURL(blob) // Create a URL for the blob

          // Create a link element and set its href to the blob URL
          const link = document.createElement('a')
          link.href = url
          link.setAttribute(
            'download',
            `transaction_verification_boogi_${transactionVerification.id}.csv`
          ) // Set the desired file name

          document.body.appendChild(link)
          link.click() // Programmatically click the link to trigger download

          document.body.removeChild(link) // Remove the link after the download is triggered
        } else {
          console.error('Failed to download CSV')
        }
      } catch (error) {
        console.error('Error downloading CSV:', error)
      } finally {
        this.loading = false // Stop loading
      }
    },

    async deleteOffer() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/offer/delete/${this.activatedOffer.id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          // Return to the offers catalogue (not the retailer page) and confirm with a toast.
          toast.success('Offer removed from your program')
          this.$router.push('/cashback-program/offer-catalogue')
        } else {
          toast.error('Could not remove the offer. Please try again.')
        }
      } catch (error) {
        toast.error('Could not remove the offer. Please try again.')
      }
    },

    async deleteBYOOOffer() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/bring-your-own-offer/offer/${this.offerId}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          toast.success('Offer deleted')
          this.$router.push('/custom-offers')
        } else {
          toast.error('Could not delete the offer. Please try again.')
        }
      } catch (error) {
        toast.error('Could not delete the offer. Please try again.')
      }
    },

    async getOffer() {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/catalogue/offer/${this.offerId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.offer = object
          this.setBreadcrumb()
          this.getActivatedOffer()
        } else {
          this.error = true
          this.loading = false
        }
      } catch (e) {
        this.error = true
        this.loading = false
      }
    },

    async getActivatedOffer() {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/offer/get/${this.offerId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.activatedOfferData = []
          this.activatedOffer = object
          this.activatedOfferData.push(this.activatedOffer)

          this.offer.cashbackRules = this.activatedOffer.cashbackEarnings
          this.getTransactionVerifications(0, 25)
        } else {
          // The offer hasn't been requested/activated yet — a valid state, not an
          // error. Flagging it errored here broke every base-offer tab (overview,
          // conditions, matching, channels, display) which only need `offer`.
          this.activatedOffer = undefined
          this.activatedOfferData = []
        }
      } catch (e) {
        this.activatedOffer = undefined
        this.activatedOfferData = []
      } finally {
        this.loading = false
        // Load the offer's cashback ledger regardless of activation state so the
        // ledger tab doesn't sit stuck loading for a not-yet-requested offer.
        this.getCashbackObjects(0, 25)
      }
    },
  },
  computed: {
    // Cashback-status filter for the ledger's native DataTable filter.
    statusFilterGroups() {
      return [
        {
          key: 'status',
          label: 'Cashback status',
          options: this.statuses.map((s) => ({ value: s.id, label: s.text })),
        },
      ]
    },
    statusFilters() {
      return { status: this.selectStatus.map((s) => s.id) }
    },

    // Approval was declined: the offer cannot be made live or edited.
    offerDeclined() {
      return !!(this.activatedOffer && this.activatedOffer.offerApproval === 'DECLINED')
    },

    // A custom offer is one this publisher supplies themselves (same check that gates the
    // Edit/Delete offer buttons and the Payouts tab).
    isCustomOffer() {
      return !!(this.offer && this.publisherId && this.offer.supplier === this.publisherId)
    },

    // The headline figure shown in the API preview card. Percentage offers render as
    // "5%", fixed-amount offers as "£10.00" (the old markup printed a bare number for
    // FIXED, with no currency symbol at all).
    displayCashback() {
      const value =
        this.offer &&
        this.offer.cashbackRules &&
        this.offer.cashbackRules[0] &&
        this.offer.cashbackRules[0].conditions &&
        this.offer.cashbackRules[0].conditions[0] &&
        this.offer.cashbackRules[0].conditions[0].value
      if (!value || value.endUserValue == null) return '—'
      const currency = this.getCurrencySymbol(this.offer.currencyList)
      return `${this.cashbackPrefix(value.type, currency)}${Number(value.endUserValue).toFixed(2)}${this.cashbackSuffix(value.type)}`
    },

    getBestCashbackRule() {
      let best = null
      // getActivatedOffer overwrites cashbackRules with the program offer's
      // cashbackEarnings, which can be absent — iterating it blind whitescreened
      // the whole page.
      const rules = (this.offer && this.offer.cashbackRules) || []
      rules.forEach((rule) => {
        const conditions = rule.conditions || []
        conditions.forEach((cond) => {
          const val = cond.value?.endUserValue || 0

          if (!best || val > best.value) {
            best = {
              value: val,
              rule,
              condition: cond,
            }
          }
        })
      })

      return best
    },

    selectedSegments() {
      if (this.activatedOffer) {
        const uniqueTexts = Array.from(
          new Set(
            this.activatedOffer.selectedCustomerTiers
              .map((id) => {
                const match = this.customerSegments.find((seg) => seg.id === id)
                return match ? match.text : null
              })
              .filter(Boolean)
          )
        )

        return uniqueTexts
      } else {
        return []
      }
    },

    formattedRetailerWebsite() {
      if (this.offer.retailer.retailerWebsite == null) {
        return null
      }

      // Check if retailerWebsite starts with "https://" or "http://"
      if (
        this.offer.retailer.retailerWebsite.startsWith('https://') ||
        this.offer.retailer.retailerWebsite.startsWith('http://')
      ) {
        return this.offer.retailer.retailerWebsite
      } else {
        // If it doesn't, add "https://" prefix
        return `https://${this.offer.retailer.retailerWebsite}`
      }
    },
    // Remove quotation marks from the description. Guarded because description is optional:
    // calling .replace() on a null threw inside the computed, which failed the whole
    // Display information card's render and left it blank.
    // Only ever link out to http(s). A stored javascript:/data: URI must never become an href
    // (Sentrium BOOGI-001 stored XSS); the backend rejects these now but old data may remain.
    safeOfferLink() {
      const link = this.offer?.displayInformation?.link
      return typeof link === 'string' && /^https?:\/\//i.test(link.trim()) ? link.trim() : ''
    },
    sanitizedDescription() {
      const description =
        this.offer && this.offer.displayInformation && this.offer.displayInformation.description
      if (!description) return ''
      return String(description).replace(/"/g, '')
    },
  },
  mounted() {
    document.title = 'Dashboard | Cashback offer'
  },
  watch: {
    activateOfferRequest: {
      deep: true,
      handler: function (newValue) {
        if (newValue.splitOfCashbackPercentage.endUser == '') {
          this.activateOfferRequest.splitOfCashbackPercentage.endUser = 0
        }
        if (newValue.splitOfCashbackPercentage.endUser > 100) {
          this.activateOfferRequest.splitOfCashbackPercentage.endUser = 100
          this.pecentageNotRight = 'The percentage needs to be less than or equal to 100'
        } else if (newValue.splitOfCashbackPercentage.endUser < 0) {
          this.activateOfferRequest.splitOfCashbackPercentage.endUser = 0
          this.pecentageNotRight = 'The percentage needs to be greater than or equal to 0'
        }
        this.pecentageNotRight = ''
        this.activateOfferRequest.splitOfCashbackPercentage.client =
          100 - newValue.splitOfCashbackPercentage.endUser
      },
    },
    serverOptions: {
      deep: true,
      handler(val) {
        this.getCashbackObjects(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
